import { dataLayerPush } from "@/shared/utils/analytics";
import { analyticsPageObj, analyticsTicketObj, analyticsUserObj } from "./composables/analytics/use-general-entities";
import useAnalyticsUtils from "./composables/analytics/use-analytics-utils";
import {
    SearchResults,
    SearchClick,
    AddToFavoritesAnalytics,
    AccordionAnalytics,
    AnalyticsBet,
    AnalyticsTicket,
} from "./model/analytics/entities";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import { cloneDeep as _cloneDeep } from "lodash";
import { Constants } from "./model/enums/analytics/constants";
import { BetData } from "./model/bet";
import { BetLabel } from "./model/enums/analytics/bet";
import {
    EventsDrilldownDetailEvent,
    EventsDrilldownDetailEventMarket,
} from "@/live/model/events-drilldown-detail-event";
import { PageType } from "./model/enums/analytics/page";
import { PriceChangeBehaviour } from "./model/ticket";

const debug = false; // set to true to see the dataLayer object in the console
type AnalyticsFunction = () => Promise<void>;

class Analytics {
    private readonly setAnalyticsFavouriteSports: AnalyticsFunction;
    private readonly setTicketGeneral: AnalyticsFunction;
    private readonly setTicketBetsGeneral: AnalyticsFunction;
    private readonly transformTicketToAnalyticsTicketObj: (
        ticket: BetData,
        includePrices?: boolean,
        cashoutableOnly?: boolean,
    ) => AnalyticsTicket;

    private readonly getLowestMarketPrices: (markets: EventsDrilldownDetailEventMarket[]) => string | undefined;

    constructor() {
        const {
            setAnalyticsFavouriteSports,
            setTicketGeneral,
            setTicketBetsGeneral,
            transformTicketToAnalyticsTicketObj,
            getLowestMarketPrices,
        } = useAnalyticsUtils();
        this.setAnalyticsFavouriteSports = setAnalyticsFavouriteSports;
        this.setTicketGeneral = setTicketGeneral;
        this.setTicketBetsGeneral = setTicketBetsGeneral;
        this.getLowestMarketPrices = getLowestMarketPrices;
        this.transformTicketToAnalyticsTicketObj = transformTicketToAnalyticsTicketObj;
    }

    //DL2.00 - Page view
    async sendPageView() {
        await this.setAnalyticsFavouriteSports();
        await this.setTicketBetsGeneral();
        await this.setTicketGeneral();
        const dataLayerObject = {
            page: analyticsPageObj,
            ticket: analyticsTicketObj,
            event: "pageReady",
        };

        this.pushDataLayer(dataLayerObject, "Page ready");
    }

    //DL3.00 - Search - main search bar
    sendSearch(searchResults: SearchResults) {
        analyticsPageObj.type = PageType.ListSearch;
        const dataLayerObject = {
            event: "page",
            page: {
                ...analyticsPageObj,
                list: {
                    ...searchResults,
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Search");
        analyticsPageObj.type = PageType.List;
    }

    //DL3.05 - Search Click - main search bar
    sendSearchClick(searchClickObj: SearchClick) {
        const dataLayerObject = {
            action: {
                click: { ...searchClickObj },
            },
            event: "action.click.search",
        };

        this.pushDataLayer(dataLayerObject, "Search Click");
    }

    //DL3.10 - Apply filter
    sendApplyFilter(filter: string) {
        const dataLayerObject = {
            action: {
                applyFilter: {
                    eventProperty1: filter,
                },
            },
            page: {
                appliedFilters: filter,
            },
            event: "action.applyFilter.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Apply filter");
    }

    //DL3.15 - Add to favorites
    sendAddToFavorites(favorites: AddToFavoritesAnalytics) {
        const dataLayerObject = {
            action: {
                addToFavorite: favorites,
            },
            event: "action.addToFavorite.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Add to favorites");
    }

    //DL3.20 - Collapsing detail
    sendCollapsingDetail(collapseAnalytics: AccordionAnalytics) {
        //TODO: Known issue - exanding detail is sending on live page even if it is not collapsing. The issue will be solved by redesign of the accordions
        const dataLayerObject = {
            action: {
                collapseDetail: collapseAnalytics,
            },
            event: "action.collapseDetail.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Collapsing detail");
    }

    //DL3.25 - Expanding detail
    sendExpandingDetail(collapseAnalytics: AccordionAnalytics) {
        const dataLayerObject = {
            action: {
                expandDetail: collapseAnalytics,
            },
            event: "action.expandDetail.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Expanding detail");
    }

    //DL3.30 - Add bet to ticket
    async sendAddBetToTicket(bet: AnalyticsBet) {
        const dataLayerObject = {
            action: {
                add_to_cart: bet,
            },
            ticket: analyticsTicketObj,
            event: "action.add_to_cart.product",
        };

        this.pushDataLayer(dataLayerObject, "Add bet to ticket");
    }

    //DL3.35 - Remove bet from ticket
    async sendRemoveBetFromTicket(bet: AnalyticsBet) {
        const dataLayerObject = {
            action: {
                remove_from_cart: bet,
            },
            ticket: analyticsTicketObj,
            event: "action.remove_from_cart.product",
        };

        this.pushDataLayer(dataLayerObject, "Remove bet from ticket");
    }

    //DL3.36 - Bet money
    async sendBetMoney(ticketType?: string, ticketStake?: string) {
        analyticsTicketObj.type = ticketType || Constants.NotApplied;
        analyticsTicketObj.totalDeposit = ticketStake || Constants.NotApplied;

        const dataLayerObject = {
            event: "action.bet.money",
            ticket: analyticsTicketObj,
        };

        this.pushDataLayer(dataLayerObject, "Bet money");
    }

    //DL3.40 - Page Category Navigation
    //changed from action.pageCategoryNavigation to this for some reason - without notice. I might be wrong, so leaving this as a comment to remind me to check it later
    async sendPageCategoryNavigation(label: string, category: string) {
        const dataLayerObject = {
            event: "action.click.navigation",
            action: {
                click: {
                    label: label, // clicked option text
                    eventProperty1: category, // this is used to differentiate between the main navigation and bottom navigation
                },
            },
        };
        this.pushDataLayer(dataLayerObject, "Page category navigation");
    }

    //DL3.50 - Edit Favourite Sports
    async sendEditFavouriteSports() {
        await this.setAnalyticsFavouriteSports();
        const dataLayerObject = {
            event: "action.editFavoriteSports.sazkabet",
            action: {
                editFavoriteSports: {
                    eventProperty1: analyticsPageObj.favoriteSports,
                },
            },
            page: analyticsPageObj,
        };

        this.pushDataLayer(dataLayerObject, "Edit favourite sports");
    }

    //DL3.55 - Pin Bet - DESKTOP ONLY
    async sendPinBet() {
        const dataLayerObject = {
            event: "action.pinBet.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Pin bet");
    }

    //DL3.60 - Change Opportunity type - MOBILE ONLY
    async sendChangeOpportunityType(opportunity: string) {
        analyticsPageObj.opportunityType = opportunity;
        const dataLayerObject = {
            event: "action.changeOpportunityType.sazkabet",
            action: {
                changeOpportunityType: {
                    eventProperty1: opportunity,
                },
            },
            page: analyticsPageObj,
        };

        this.pushDataLayer(dataLayerObject, "Change opportunity type");
    }

    //DL3.65 - Display Odds Switch - DESKTOP ONLY
    async sendChangeDisplayOdds() {
        const dataLayerObject = {
            event: "action.displayOdds.sazkabet",
            action: {
                displayOdds: {
                    eventProperty1: analyticsPageObj.displayOdds.toString(),
                },
            },
            page: analyticsPageObj,
        };

        this.pushDataLayer(dataLayerObject, "Change display odds");
    }

    //DL3.70 - Display Score Switch - MOBILE ONLY
    async sendChangeDisplayScore() {
        const dataLayerObject = {
            event: "action.displayScore.sazkabet",
            action: {
                displayOdds: {
                    eventProperty1: analyticsPageObj.displayScore,
                },
            },
            page: analyticsPageObj,
        };

        this.pushDataLayer(dataLayerObject, "Change display score");
    }
    //DL4.00 - Side menu open impression - MOBILE ONLY
    async sendSideMenuOpenImpression() {
        const dataLayerObject = {
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "sideMenu",
                            name: "sideMenu.kurzovesazky",
                            creative: "Menu",
                            position: "sideMenu",
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Side menu click");
    }

    //DL4.05 - Side Menu Click - MOBILE ONLY
    async sendSideMenuClick(itemName: string, index: number) {
        const dataLayerObject = {
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "sideMenu",
                            name: "sideMenu.kurzovesazky",
                            creative: itemName, // text from clicked element or <url of the banner>
                            position: "sideMenu." + index.toString(), // sideMenu. + position of clicked element
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Side menu click");
    }

    //DL4.20 - Nice. Highest Win Banner Impression
    async sendHighestWinBannerImpression(highestWin: string, position: string) {
        const dataLayerObject = {
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "highestWin",
                            name: "highestWin",
                            creative: highestWin, // amount from banner
                            position: "ticket." + position, // ticket.1|2|3
                        },
                    ],
                },
            },
        };
        this.pushDataLayer(dataLayerObject, "Send highest win impression");
    }

    //DL4.30- Show error event
    async sendErrorEvent(text: string, type: string = "banner") {
        const dataLayerObject = {
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "error." + type, // error impression type (1-popup, 2-banner)
                            name: "error", // const
                            creative: text,
                            position: type === "banner" ? "top" : "middle",
                        },
                    ],
                },
            },
        };
        this.pushDataLayer(dataLayerObject, "Send Error Event");
    }

    //DL4.31 - Change ticket settings
    async sendChangeTicketSettings(isMobile) {
        const oddsSettingsDict = {
            [PriceChangeBehaviour.HIGHER]: "acceptHigher",
            [PriceChangeBehaviour.ALL]: "acceptAll",
            [PriceChangeBehaviour.NONE]: "rejectAll",
        };

        const formattedOddSetting = oddsSettingsDict[analyticsUserObj.oddSettings] ?? analyticsUserObj.oddSettings;

        const dataLayerObject = {
            event: "action.changeTicketSettings.sazkabet",
            user: {
                oddSettings: formattedOddSetting, // Use the formatted value here!
                showMiniTicket: isMobile ? analyticsUserObj.showMiniTicket : Constants.NotApplied,
            },
        };

        this.pushDataLayer(dataLayerObject, "Change ticket settings");
    }

    //DL4.34 - Deposit change
    async sendDepositChange(stake, fromButton = false) {
        const dataLayerObject = {
            event: "action.changeDeposit.ticket",
            action: {
                changeDeposit: {
                    eventProperty1: fromButton ? "button" : "manual", // manual | button
                    eventProperty2: stake.toString(),
                },
            },
            ticket: {
                totalDeposit: stake.toString(),
            },
        };

        this.pushDataLayer(dataLayerObject, "Send deposit change");
    }

    //DL4.35 - Impress last searched items
    async sendImpressLastSearchedItems(impressedItems: string[]) {
        const dataLayerObject = {
            event: "action.impressLastSearch.sazkabet",
            action: {
                impressLastSearch: {
                    eventProperty1: impressedItems.join(","),
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Impress last searched items");
    }

    //DL4.36 - Click last searched items
    async sendClickLastSearchedItem(query) {
        const dataLayerObject = {
            event: "action.clickLastSearch.sazkabet",
            action: {
                clickLastSearch: {
                    eventProperty1: query,
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Click last searched item");
    }

    //DL4.37 - Duplicate ticket
    async sendDuplicateTicket() {
        await this.setTicketBetsGeneral();
        await this.setTicketGeneral();

        const dataLayerObject = {
            event: "action.duplicate.ticket",
            ticket: analyticsTicketObj,
        };

        this.pushDataLayer(dataLayerObject, "Duplicate ticket");
    }

    //DL4.38 - Share ticket //
    async sendShareTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket);
        const dataLayerObject = {
            event: "action.share.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Share ticket");
    }

    //DL4.39 - Send ticket return (refund)
    async sendReturnTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket);

        const dataLayerObject = {
            event: "action.return.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Refund ticket");
    }

    //DL4.40 - Send ticket cashout
    async sendCashoutTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket, false, true);

        const dataLayerObject = {
            event: "action.clickCashout.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Cashout click ticket");
    }

    //DL4.41 - Confirm cashout ticket
    async sendConfirmCashoutTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket, false, true);

        const dataLayerObject = {
            event: "action.confirmCashout.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Cashout confirm ticket");
    }

    //DL4.42 - Cancel cashout ticket
    async sendCancelCashoutTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket, false, true);

        const dataLayerObject = {
            event: "action.cancelCashout.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Cashout cancel ticket");
    }

    //DL4.43 - Successful cashout ticket
    async sendSuccessCashoutTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket, false, true);

        const dataLayerObject = {
            event: "action.successfulCashout.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Cashout click ticket");
    }

    //DL4.44 - Fail cashout ticket
    async sendFailCashoutTicket(ticket) {
        const transformedTicket = this.transformTicketToAnalyticsTicketObj(ticket, false, true);

        const dataLayerObject = {
            event: "action.failedCashout.ticket",
            ticket: transformedTicket,
        };

        this.pushDataLayer(dataLayerObject, "Cashout failed ticket");
    }

    //DL4.45a - RETURN OF THE DL44 - Homepage banner impress
    async sendHomepageBannerImpress(position, slide, index) {
        const dataLayerObject = {
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: position,
                            name: slide.titleDesktop,
                            creative: slide.text,
                            position: index.toString(), //number of the image in the carousel list
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Homepage banner impression");
    }

    //DL4.45b - SHOW PROMO - Homepage banner show (but click)
    async sendHomepageBannerShow(position, slide, index) {
        const dataLayerObject = {
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: position,
                            name: slide.titleDesktop,
                            creative: slide.text,
                            position: index.toString(), //number of the image in the carousel list
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Homepage banner click");
    }

    //DL4.46 - Impress bets
    async sendImpressBets(event: EventsDrilldownDetailEvent, index: number) {
        const lowestDisplayorderMarketPrices = this.getLowestMarketPrices(event.markets);

        const dataLayerObject = {
            event: "action.impress.products",
            action: {
                impress: {
                    products: [
                        {
                            label: event.started ? BetLabel.Live : BetLabel.Prematch,
                            group: "A",
                            gameCode: event.drilldownNodes[2].code,
                            sportLeague: event.drilldownNodes[0].name,
                            matchName: event.name,
                            betType: event?.markets?.[0]?.name,
                            quote: lowestDisplayorderMarketPrices || "?",
                            betPlacement: "main",
                            betCategory: "popular",
                            eventProperty1: index.toString(),
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Homepage impress bets");
    }

    //DL4.46a - Show bet
    async sendShowBet(event: EventsDrilldownDetailEvent, index: number) {
        const lowestDisplayorderMarketPrices = this.getLowestMarketPrices(event.markets);

        const dataLayerObject = {
            event: "action.show.products",
            action: {
                show: {
                    products: [
                        {
                            label: event.started ? BetLabel.Live : BetLabel.Prematch,
                            group: "A",
                            gameCode: event.drilldownNodes[2].code,
                            sportLeague: event.drilldownNodes[0].name,
                            matchName: event.name,
                            betType: event?.markets?.[0]?.name,
                            quote: lowestDisplayorderMarketPrices || "?",
                            betPlacement: "main",
                            betCategory: "popular",
                            eventProperty1: index.toString(),
                        },
                    ],
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Homepage click bets");
    }

    //DL4.47 - Click weekly bet
    async sendClickBestWeeklyBets() {
        const dataLayerObject = {
            event: "action.clickWeeklyTopTickets.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Click weekly bets");
    }

    //DL4.48 - Show competitions
    async sendShowCompetitions() {
        const dataLayerObject = {
            event: "action.expandCompetitions.sazkabet",
        };

        this.pushDataLayer(dataLayerObject, "Send show competitions");
    }

    //DL4.49 - Click competition details
    async sendClickCompetitionDetails(title: string, text: string) {
        const dataLayerObject = {
            event: "action.showDetailCompetitions.sazkabet",
            action: {
                showDetailCompetitions: {
                    eventProperty1: title, // competition name first row
                    eventProperty2: text, // competition name second row
                },
            },
        };

        this.pushDataLayer(dataLayerObject, "Send show competitions");
    }

    async pushDataLayer(dataLayerObject, message?: string) {
        if (isDevelopmentEnv() && debug) {
            console.log("---------------- " + message + "  ----------------");
            const dataLayerObjectCopy = _cloneDeep(dataLayerObject);
            console.log(dataLayerObjectCopy, "real data");
            console.log(dataLayerObject, "proxy data");
        }

        dataLayerPush(dataLayerObject);
    }
}

const analytics = new Analytics();
export default analytics;
